import { Link } from 'react-router-dom';

const BRAND = {
  accent: '#2C5F8A',
  accentDeep: '#1A4060',
  accentLight: '#E8F0F8',
  bg: '#F6F5F3',
  card: '#FFFFFF',
  border: '#E2E0DA',
  ink: '#1A1A1A',
  inkSec: '#5C5C5C',
  serif: "'Newsreader', Georgia, serif",
};

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2
      style={{
        fontFamily: BRAND.serif,
        fontSize: 22,
        fontWeight: 500,
        color: BRAND.ink,
        margin: 0,
      }}
    >
      {title}
    </h2>
    <div className="space-y-3 text-sm leading-6" style={{ color: BRAND.inkSec }}>
      {children}
    </div>
  </section>
);

const List = ({ children }) => (
  <ul className="list-disc space-y-2 pl-5">
    {children}
  </ul>
);

const Shell = ({ eyebrow, title, children }) => (
  <div
    className="min-h-screen px-4 pb-20 pt-8 sm:px-6 lg:px-8"
    style={{ background: BRAND.bg }}
  >
    <main className="mx-auto max-w-3xl">
      <Link
        to="/login"
        className="mb-6 inline-flex text-sm font-semibold hover:underline"
        style={{ color: BRAND.accent }}
      >
        Back to login
      </Link>
      <article
        className="space-y-8 rounded-2xl p-6 sm:p-10"
        style={{
          background: BRAND.card,
          border: `1px solid ${BRAND.border}`,
          boxShadow:
            '0 1px 2px rgba(26, 64, 96, 0.04), 0 12px 32px rgba(26, 64, 96, 0.10)',
        }}
      >
        <header className="space-y-3">
          <p
            className="text-xs font-semibold uppercase tracking-[0.08em]"
            style={{ color: BRAND.accentDeep, margin: 0 }}
          >
            {eyebrow}
          </p>
          <h1
            style={{
              fontFamily: BRAND.serif,
              fontSize: 34,
              fontWeight: 500,
              color: BRAND.ink,
              margin: 0,
            }}
          >
            {title}
          </h1>
          <p className="text-sm" style={{ color: BRAND.inkSec, margin: 0 }}>
            Last updated: June 5, 2026
          </p>
        </header>
        {children}
      </article>
    </main>
  </div>
);

export const PrivacyPolicyPage = () => (
  <Shell eyebrow="Data policy" title="Privacy and data use policy">
    <Section title="Purpose and scope">
      <p>
        This policy explains how data is collected, accessed, protected, and
        used when researchers, reviewers, organization administrators, and
        platform administrators use the OncoVectra Research Platform. It
        applies to account data, research workflow data, platform-generated
        records, uploaded or assigned research materials, and support records
        created through the platform.
      </p>
      <p>
        This policy is intended to describe the platform's operating practices.
        It does not replace any signed research agreement, institutional review
        requirement, data processing agreement, or study-specific protocol. If a
        written agreement applies and conflicts with this policy, the written
        agreement controls for that study or organization.
      </p>
    </Section>

    <Section title="Administrator visibility">
      <p>
        Data generated on this platform may be accessible to authorized
        administrators for research governance, platform support, and quality
        assurance. We do not sell, share, or use this data for any third-party
        purpose.
      </p>
      <p>
        Administrator access is limited to users with assigned administrative
        roles and is intended for legitimate platform and research governance
        purposes. This may include confirming study configuration, supporting
        users, investigating access or completion issues, maintaining audit
        trails, and reviewing data quality.
      </p>
    </Section>

    <Section title="Data we collect and store">
      <p>
        Depending on how a study is configured, the platform may collect and
        store:
      </p>
      <List>
        <li>account details, including name, email address, login provider, and authentication identifiers;</li>
        <li>organization membership, roles, invitations, approvals, and access status;</li>
        <li>study configuration, assigned papers, paper metadata, review queues, and questionnaire settings;</li>
        <li>research responses, annotations, completion status, progress records, and timestamps;</li>
        <li>administrator actions, support records, audit records, error records, and security logs;</li>
        <li>technical metadata needed to operate the service, such as browser, device, network, and session metadata.</li>
      </List>
      <p>
        The platform should not be used to enter patient-identifying clinical
        data, protected health information, or confidential third-party material
        unless the relevant organization has approved that use and the required
        legal, ethical, and security controls are in place.
      </p>
    </Section>

    <Section title="How the data is used">
      <p>
        Data is used to operate the review process, manage account access,
        support administrators and reviewers, monitor completion, preserve
        research quality, diagnose platform issues, maintain security, and
        produce exports or reports requested by authorized study administrators.
      </p>
      <p>
        Platform data is not used for advertising, data brokerage, unrelated
        commercial profiling, or independent third-party research. We do not
        sell platform data.
      </p>
    </Section>

    <Section title="Service providers and third-party access">
      <p>
        We do not share platform data with advertisers, data brokers, sponsors,
        or other third parties for their independent purposes. Limited service
        providers may process data only as needed to operate the platform, such
        as hosting, authentication, storage, security monitoring, email delivery,
        debugging, backup, and support.
      </p>
      <p>
        Those service providers are not permitted to use platform data for their
        own marketing, resale, or unrelated research purposes. They may have
        access only to the extent required to provide the service and maintain
        the platform.
      </p>
    </Section>

    <Section title="Research data boundary">
      <p>
        Open-source licensing of the software does not make user accounts,
        research responses, uploaded papers, study configuration, or generated
        datasets public. Those materials remain subject to the access controls
        and research governance practices of the platform and its authorized
        administrators.
      </p>
      <p>
        The open research commitment behind this platform is a commitment to
        transparent methods, accountable governance, reproducible workflows, and
        reusable software. It is not a commitment to make private accounts,
        institutional research data, reviewer responses, or study outputs
        public without authorization.
      </p>
    </Section>

    <Section title="Retention and deletion">
      <p>
        Platform data is retained for as long as needed to provide the service,
        maintain research records, meet audit and governance obligations,
        resolve disputes, comply with law, and support study administration.
        Authorized administrators may request deletion or export of eligible
        data, subject to legal, technical, research integrity, and institutional
        retention requirements.
      </p>
      <p>
        Some records may remain in backups, logs, audit trails, or archived
        research records for a limited period after deletion from active systems
        when retention is required for security, compliance, or research
        integrity.
      </p>
    </Section>

    <Section title="Security and access controls">
      <p>
        The platform uses role-based access controls, authenticated accounts,
        administrative permissions, and operational safeguards designed to limit
        access to authorized users. No online system can guarantee absolute
        security, so users should avoid entering data that is not approved for
        the relevant study or organization.
      </p>
      <p>
        Users are responsible for keeping their login credentials secure,
        signing out of shared devices, and reporting suspected unauthorized
        access promptly.
      </p>
    </Section>

    <Section title="User rights and contact">
      <p>
        Depending on applicable law and institutional agreements, users may have
        rights to request access, correction, export, restriction, or deletion
        of personal data. Some requests may be limited by research integrity,
        audit, security, legal, or institutional obligations.
      </p>
      <p>
        Requests should be sent to the platform operator or the organization
        administrator responsible for the relevant study. The platform operator
        may need to verify identity and authority before acting on a request.
      </p>
    </Section>

    <Section title="Policy changes">
      <p>
        This policy may be updated as platform practices, legal requirements,
        infrastructure, or research governance requirements change. Material
        changes should be reflected by updating this page and, where
        appropriate, notifying affected administrators or users.
      </p>
    </Section>
  </Shell>
);

export const TermsPage = () => (
  <Shell eyebrow="Terms" title="Terms and conditions">
    <Section title="Agreement to these terms">
      <p>
        These terms govern access to and use of the OncoVectra Research
        Platform. By creating an account, signing in, administering a study, or
        using the platform, you agree to use it only for authorized research and
        review workflows and in accordance with these terms.
      </p>
      <p>
        These terms do not replace any signed research agreement, institutional
        policy, data processing agreement, or study protocol. If a written
        agreement applies and conflicts with these terms, the written agreement
        controls for that study or organization.
      </p>
    </Section>

    <Section title="Use of the platform">
      <p>
        This platform is intended for structured research review workflows.
        Users are responsible for using it lawfully, respecting institutional
        obligations, and only uploading or entering material they are authorized
        to use in the relevant study.
      </p>
      <p>
        Users must provide accurate account information, keep credentials
        secure, use only their own account, and promptly report suspected
        unauthorized access. Organization administrators are responsible for
        assigning roles, reviewing membership requests, removing users who no
        longer need access, and configuring studies consistently with approved
        research governance requirements.
      </p>
    </Section>

    <Section title="Research content and user responsibility">
      <p>
        Users are responsible for the research materials, responses, uploaded
        files, comments, metadata, and other content they submit or manage
        through the platform. Users must have the rights, permissions, and
        approvals needed to submit that content.
      </p>
      <p>
        The platform may assist with organization, assignment, review tracking,
        and export of research workflow data, but users and administrators
        remain responsible for validating outputs before relying on them for
        analysis, publication, regulatory work, or institutional reporting.
      </p>
    </Section>

    <Section title="MIT license boundary">
      <p>
        The application software may be distributed under the MIT License when
        it is made available with an MIT license notice. The MIT License permits
        broad use, copying, modification, publication, distribution,
        sublicensing, and sale of the software, subject to the copyright notice,
        license notice, and warranty disclaimer that accompany the source code.
      </p>
      <p>
        The MIT License applies to the software code only. It does not license,
        publish, or otherwise open-source user data, research responses,
        uploaded documents, paper metadata, study configuration, or
        administrator-managed content.
      </p>
    </Section>

    <Section title="Data handling and administrator access">
      <p>
        Authorized administrators may access platform-generated data for
        research governance, platform support, and quality assurance. We do not
        sell, share, or use this data for any third-party purpose.
      </p>
      <p>
        Limited service providers may process data only as needed to host,
        secure, maintain, and support the platform. That operational processing
        does not grant those providers the right to use platform data for their
        own marketing, resale, profiling, or independent research purposes.
      </p>
    </Section>

    <Section title="Acceptable use">
      <p>
        Users must not use the platform to violate law, infringe intellectual
        property rights, access data without authorization, interfere with
        platform security, reverse engineer restricted services, introduce
        malware, overload the platform, or submit content they are not permitted
        to submit.
      </p>
      <p>
        Users must not enter patient-identifying clinical data, protected health
        information, or confidential third-party material unless the relevant
        organization has approved that use and the required legal, ethical, and
        security controls are in place.
      </p>
    </Section>

    <Section title="Availability and changes">
      <p>
        The platform may be changed, suspended, restricted, or discontinued to
        maintain security, improve operation, comply with law, respond to
        governance requirements, or support research administration. Reasonable
        efforts should be made to avoid unnecessary disruption to active studies.
      </p>
      <p>
        Access may be suspended or terminated if an account is inactive,
        unauthorized, compromised, no longer associated with an approved
        organization, or used in a way that violates these terms or applicable
        agreements.
      </p>
    </Section>

    <Section title="No warranty">
      <p>
        To the maximum extent permitted by law, the software and platform are
        provided without a guarantee that they will be uninterrupted, error-free,
        secure against every threat, or suitable for every research, regulatory,
        clinical, or institutional requirement.
      </p>
      <p>
        The platform does not provide medical, clinical, legal, regulatory, or
        statistical advice. Users and administrators should obtain appropriate
        professional review before relying on platform outputs for decisions,
        submissions, publications, or reports.
      </p>
    </Section>

    <Section title="Liability and remedies">
      <p>
        To the maximum extent permitted by law, the platform operator and
        software contributors are not liable for indirect, incidental, special,
        consequential, exemplary, or punitive damages, or for lost profits, lost
        data, lost research opportunities, interruption, or unauthorized access
        caused by use of the platform.
      </p>
      <p>
        Some jurisdictions do not allow certain limitations of liability, so
        these limitations apply only to the extent permitted by applicable law
        and any controlling written agreement.
      </p>
    </Section>

    <Section title="Changes to these terms">
      <p>
        These terms may be updated as platform practices, legal requirements,
        infrastructure, or research governance requirements change. Continued
        use of the platform after updated terms are posted means the user
        accepts the updated terms, unless a controlling written agreement says
        otherwise.
      </p>
    </Section>
  </Shell>
);
